import { useQuery, useQueryClient, useMutation } from "react-query";
import API from "./utils/api";
import {
	PaymentInstitutionDto,
	PaymentInstitutionPaginator,
	CreateCompanyPaymentInstitutionDto,
	QueryPaymentInstitutionDto,
	UpdatePaymentInstitutionDto,
	SearchPaymentInstitutionDto,
} from "../models/payment-institution.dto";
import ENDPOINTS from "./utils/endpoints";
import { toast } from "react-toastify";

export function useCompanyGetPaymentInstitution(
	companyId: number,
	options?: QueryPaymentInstitutionDto,
) {
	const url =
		ENDPOINTS.BILLING_AND_SUBSCRIPTION.COMPANY_PAYMENT_INSTITUTION.GET;
	const request = API.BillingAndSubscriptionAPI.getCompanyPaymentInstitution(
		companyId,
		options,
	);

	const { data, isLoading, error, refetch } = useQuery<
		PaymentInstitutionPaginator,
		Error
	>([url, companyId], () => request, { enabled: !!companyId });

	return { data, isLoading, error, refetch };
}

export function useGetCompanyPaymentInstitutionSearch() {
	return useMutation(
		({
			companyId,
			...options
		}: { companyId: number } & SearchPaymentInstitutionDto) =>
			API.BillingAndSubscriptionAPI.getCompanyPaymentInstitutionSearch(
				companyId,
				options,
			),
		{
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetCompanyPaymentInstitutionById(
	id: number,
	companyId: number,
) {
	const url =
		ENDPOINTS.BILLING_AND_SUBSCRIPTION.COMPANY_PAYMENT_INSTITUTION.DETAIL;
	const request =
		API.BillingAndSubscriptionAPI.getCompanyPaymentInstitutionById(
			id,
			companyId,
		);

	const { data, isLoading, error } = useQuery<
		{ data: PaymentInstitutionDto },
		Error
	>([url, id, companyId], () => request, { enabled: !!id });

	return { data, isLoading, error };
}

export function useCreateCompanyPaymentInstitution() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			companyId,
			payload,
		}: {
			companyId: number;
			payload: CreateCompanyPaymentInstitutionDto;
		}) =>
			API.BillingAndSubscriptionAPI.createCompanyPaymentInstitution(
				companyId,
				payload,
			),
		{
			onSuccess: () => {
				toast.success("Record created successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.BILLING_AND_SUBSCRIPTION.COMPANY_PAYMENT_INSTITUTION.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useUpdateCompanyPaymentInstitution() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			payload,
			id,
			companyId,
		}: {
			payload: UpdatePaymentInstitutionDto;
			id: number;
			companyId: number;
		}) =>
			API.BillingAndSubscriptionAPI.updateCompanyPaymentInstitution(
				id,
				companyId,
				payload,
			),
		{
			onSuccess: () => {
				toast.success("Record updated successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.BILLING_AND_SUBSCRIPTION.COMPANY_PAYMENT_INSTITUTION.GET,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useDeleteCompanyPaymentInstitution() {
	return useMutation(
		({ id, companyId }: { id: number; companyId: number }) =>
			API.BillingAndSubscriptionAPI.deleteCompanyPaymentInstitution(
				id,
				companyId,
			),
		{
			onSuccess: () => {
				toast.success("Record deleted successfully");
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useUploadCompanyPaymentInstitutions() {
	return useMutation(
		({ payload, companyId }: { payload: FormData; companyId: number }) =>
			API.BillingAndSubscriptionAPI.uploadCompanyPaymentInstitution(companyId, payload),
		{
			onError: API.Handlers.onErrorHandler,
		},
	);
}
